import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO, SITE_URL } from "@/hooks/useSEO";
import { factoryProfiles, rfqCategories } from "@/mocks/factoryProfiles";
import { specialFactoryProducts, specialFactoryCategories } from "@/mocks/specialFactories";
import type { FactoryProfile } from "@/mocks/factoryProfiles";
import type { SpecialFactoryProduct } from "@/mocks/specialFactories";
import FactoryProfileCard from "./components/FactoryProfileCard";
import RFQModal from "./components/RFQModal";
import FactoryChatModal from "./components/FactoryChatModal";

// ─── Product Detail Modal ─────────────────────────────────────────────────────
function ProductDetailModal({ product, onClose, onOrder }: {
  product: SpecialFactoryProduct;
  onClose: () => void;
  onOrder: () => void;
}) {
  const cat = specialFactoryCategories.find(c => c.id === product.category);
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="relative w-full h-56 overflow-hidden rounded-t-2xl">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
          <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-white/90 rounded-full text-gray-600 hover:text-gray-900 cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
          {product.badge && (
            <div className="absolute top-3 left-3 bg-amber-400 text-white text-xs font-bold px-3 py-1 rounded-full">{product.badge}</div>
          )}
        </div>
        <div className="p-6">
          {cat && (
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${cat.color} mb-3 inline-flex items-center gap-1`}>
              <i className={cat.icon}></i>{cat.label}
            </span>
          )}
          <h2 className="font-extrabold text-gray-900 text-lg mt-2 mb-2">{product.name}</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-4">{product.description}</p>

          {/* Specs */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            {product.specs.map(spec => (
              <div key={spec} className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
                <i className="ri-checkbox-circle-line text-green-500 text-sm shrink-0"></i>
                <span className="text-xs font-semibold text-gray-700">{spec}</span>
              </div>
            ))}
          </div>

          {/* Factory */}
          <div className="bg-amber-50 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <i className="ri-map-pin-line text-amber-500 mt-0.5 shrink-0"></i>
              <div>
                <p className="text-sm font-bold text-amber-700">{product.sellerFlag} {product.seller}</p>
                <p className="text-xs text-amber-600">{product.factoryAddress}</p>
                <p className="text-xs text-gray-500 mt-1">Ships from {product.sellerCountry} · Worldwide delivery via UPS/DHL</p>
              </div>
            </div>
          </div>

          {/* Rating */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center gap-0.5">
              {[1,2,3,4,5].map(s => (
                <i key={s} className={`${s <= Math.round(product.rating) ? "ri-star-fill" : "ri-star-line"} text-amber-400 text-sm`}></i>
              ))}
            </div>
            <span className="text-sm font-bold text-gray-700">{product.rating}</span>
            <span className="text-sm text-gray-400">({product.reviews.toLocaleString()} reviews)</span>
            <span className="text-sm text-gray-400">·</span>
            <span className="text-sm text-gray-400">{product.sold.toLocaleString()} sold</span>
          </div>

          {/* Price */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <span className="text-3xl font-extrabold text-amber-500">${product.price}</span>
              <span className="text-gray-400 text-sm">/{product.priceUnit}</span>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">Min. Order</p>
              <p className="text-sm font-bold text-gray-700">{product.minOrder} {product.minOrderUnit}</p>
            </div>
          </div>

          <button
            onClick={onOrder}
            className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-base"
          >
            <i className="ri-shopping-cart-line mr-2"></i>Place Sample Order
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Product Card (compact) ───────────────────────────────────────────────────
function ProductCard({ product, onClick }: { product: SpecialFactoryProduct; onClick: () => void }) {
  const cat = specialFactoryCategories.find(c => c.id === product.category);
  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-100 rounded-xl overflow-hidden cursor-pointer group hover:-translate-y-1 transition-transform duration-300"
    >
      <div className="w-full h-40 overflow-hidden relative">
        <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
        {product.badge && (
          <div className="absolute top-2 left-2 bg-amber-400 text-white text-xs font-bold px-2 py-0.5 rounded-full">{product.badge}</div>
        )}
      </div>
      <div className="p-3">
        {cat && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${cat.color} mb-1.5 inline-flex items-center gap-1`}>
            <i className={cat.icon}></i>{cat.label}
          </span>
        )}
        <h3 className="font-semibold text-gray-800 text-sm line-clamp-2 mb-1 group-hover:text-amber-500 transition-colors">{product.name}</h3>
        <div className="flex items-center justify-between">
          <span className="text-amber-500 font-extrabold">${product.price}<span className="text-xs text-gray-400 font-normal">/{product.priceUnit}</span></span>
          <span className="text-xs text-gray-400">{product.sold.toLocaleString()} sold</span>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function SolarEVPage() {
  useSEO({
    title: "Solar Energy & EV Vehicles Factory Hub | CrownGlobal Direct Manufacturer",
    description: "Source solar panels, batteries, inverters, golf carts, motorcycles, and e-bikes directly from verified factories. Request bulk quotes, chat with manufacturers, and place sample orders worldwide.",
    keywords: "solar panel factory, EV vehicles manufacturer, golf cart factory, e-bike wholesale, solar battery supplier",
    canonical: "/solar-ev",
    ogTitle: "Solar Energy & EV Vehicles Factory Hub | CrownGlobal",
    ogDescription: "Direct factory sourcing for solar panels, batteries, inverters, golf carts, motorcycles, and e-bikes. 500+ verified manufacturers.",
    schemaJson: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${SITE_URL}/solar-ev`,
          "url": `${SITE_URL}/solar-ev`,
          "name": "Solar Energy & EV Vehicles Factory Hub — CrownGlobal",
          "description": "Direct factory sourcing for solar panels, batteries, inverters, golf carts, motorcycles, and e-bikes from 500+ verified manufacturers.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
              { "@type": "ListItem", "position": 2, "name": "Solar & EV Hub", "item": `${SITE_URL}/solar-ev` }
            ]
          }
        },
        {
          "@type": "Service",
          "@id": `${SITE_URL}/solar-ev#service`,
          "url": `${SITE_URL}/solar-ev`,
          "name": "Solar Panel & EV Vehicle Factory Sourcing",
          "description": "Source solar panels, batteries, inverters, golf carts, motorcycles, scooters, and e-bikes directly from 500+ ISO/CE/SGS certified factories.",
          "provider": { "@id": `${SITE_URL}/#organization` },
          "areaServed": "Worldwide",
          "serviceType": ["Solar Panel Sourcing", "EV Vehicle Wholesale", "Factory Direct Procurement", "Bulk Quote Request"],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Solar & EV Product Categories",
            "itemListElement": [
              { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Solar Panels", "description": "Monocrystalline, bifacial, and flexible solar panels from certified Chinese factories" } },
              { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Solar Batteries", "description": "LiFePO4 and gel deep cycle batteries for solar energy storage" } },
              { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Solar Inverters", "description": "Hybrid, string, and micro inverters for residential and commercial solar systems" } },
              { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Golf Carts", "description": "Electric 4-seat and 6-seat golf carts and utility cargo vehicles" } },
              { "@type": "Offer", "itemOffered": { "@type": "Product", "name": "Electric Motorcycles & Scooters", "description": "Electric motorcycles, scooters, and e-bikes from verified EV factories" } }
            ]
          }
        }
      ]
    },
  });

  const [activeTab, setActiveTab] = useState<"factories" | "products">("factories");
  const [activeCat, setActiveCat] = useState("all");
  const [rfqFactory, setRfqFactory] = useState<FactoryProfile | null | undefined>(undefined);
  const [chatFactory, setChatFactory] = useState<FactoryProfile | null>(null);
  const [detailProduct, setDetailProduct] = useState<SpecialFactoryProduct | null>(null);
  const [showGlobalRFQ, setShowGlobalRFQ] = useState(false);

  const solarEvCats = ["solar-panel", "solar-battery", "solar-inverter", "golf-cart", "motorcycle", "scooter-ebike"];
  const filteredProducts = activeCat === "all"
    ? specialFactoryProducts.filter(p => solarEvCats.includes(p.category))
    : specialFactoryProducts.filter(p => p.category === activeCat);

  const catTabs = [
    { id: "all", label: "All", icon: "ri-apps-line" },
    { id: "solar-panel", label: "Solar Panels", icon: "ri-sun-line" },
    { id: "solar-battery", label: "Batteries", icon: "ri-battery-charge-line" },
    { id: "solar-inverter", label: "Inverters", icon: "ri-flashlight-line" },
    { id: "golf-cart", label: "Golf Carts", icon: "ri-car-line" },
    { id: "motorcycle", label: "Motorcycles", icon: "ri-motorbike-line" },
    { id: "scooter-ebike", label: "E-Bikes", icon: "ri-e-bike-line" },
  ];

  return (
    <div className="min-h-screen bg-white pb-16 md:pb-0">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/4f4568c53938964bc64f9bb893e7f66d.jpg"
            alt="Solar Energy & EV Hub"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/40"></div>
        </div>
        <div className="relative max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-1 h-6 bg-amber-400 rounded-full"></span>
              <span className="text-amber-400 text-sm font-bold uppercase tracking-widest">Verified Factory Direct</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Solar Energy &amp; EV Vehicles<br />
              <span className="text-amber-400">Factory Hub</span>
            </h1>
            <p className="text-white/80 text-base md:text-lg mb-8 leading-relaxed">
              Source solar panels, batteries, inverters, golf carts, motorcycles &amp; e-bikes directly from 500+ certified factories. Chat with manufacturers, request bulk quotes, and place sample orders today.
            </p>
            {/* Stats */}
            <div className="flex flex-wrap gap-4 mb-8">
              {[
                { value: "500+", label: "Verified Factories" },
                { value: "50GW+", label: "Solar Capacity" },
                { value: "80+ Countries", label: "Shipping Reach" },
                { value: "24h", label: "Quote Response" },
              ].map(s => (
                <div key={s.label} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3 text-center">
                  <p className="text-white font-extrabold text-lg">{s.value}</p>
                  <p className="text-white/70 text-xs">{s.label}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowGlobalRFQ(true)}
                className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-8 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap text-base"
              >
                <i className="ri-file-list-3-line"></i> Request Bulk Quote (RFQ)
              </button>
              <button
                onClick={() => setActiveTab("factories")}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-bold px-8 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-building-2-line"></i> Browse Factories
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-amber-400 py-4">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {[
              { icon: "ri-shield-check-line", label: "ISO / CE / SGS Certified Factories" },
              { icon: "ri-message-3-line", label: "Instant Factory Chat" },
              { icon: "ri-file-list-3-line", label: "Free RFQ to 500+ Factories" },
              { icon: "ri-truck-line", label: "Worldwide Shipping" },
              { icon: "ri-lock-line", label: "Secure Payment Protection" },
            ].map(b => (
              <div key={b.label} className="flex items-center gap-2 text-white">
                <i className={`${b.icon} text-lg`}></i>
                <span className="text-sm font-semibold whitespace-nowrap">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main content */}
      <section className="py-14">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">

          {/* Tab switcher */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1 w-fit mb-10">
            {[
              { key: "factories", icon: "ri-building-2-line", label: "Factory Profiles" },
              { key: "products", icon: "ri-shopping-bag-3-line", label: "Product Catalog" },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <i className={tab.icon}></i>{tab.label}
              </button>
            ))}
          </div>

          {/* ── Factory Profiles Tab ── */}
          {activeTab === "factories" && (
            <>
              <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-8">
                <div>
                  <h2 className="text-2xl font-extrabold text-gray-900 mb-1">Top Verified Factories</h2>
                  <p className="text-gray-500 text-sm">ISO, CE, SGS certified manufacturers with full production transparency</p>
                </div>
                <button
                  onClick={() => setShowGlobalRFQ(true)}
                  className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap shrink-0"
                >
                  <i className="ri-send-plane-line"></i> Send RFQ to All Factories
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
                {factoryProfiles.map(factory => (
                  <FactoryProfileCard
                    key={factory.id}
                    factory={factory}
                    onContact={f => setChatFactory(f)}
                    onRFQ={f => setRfqFactory(f)}
                  />
                ))}
              </div>

              {/* Certification legend */}
              <div className="bg-gray-50 rounded-2xl p-8">
                <h3 className="font-extrabold text-gray-900 text-lg mb-2">Understanding Factory Certifications</h3>
                <p className="text-gray-500 text-sm mb-6">All factories on CrownGlobal are verified against international standards. Here's what each certification means for you as a buyer.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { cert: "ISO 9001", desc: "Quality Management System — ensures consistent product quality and process control", color: "bg-sky-50 border-sky-200 text-sky-700" },
                    { cert: "ISO 14001", desc: "Environmental Management — factory meets international environmental standards", color: "bg-green-50 border-green-200 text-green-700" },
                    { cert: "CE Mark", desc: "European Conformity — product meets EU safety, health, and environmental requirements", color: "bg-amber-50 border-amber-200 text-amber-700" },
                    { cert: "SGS Verified", desc: "Third-party inspection by SGS — world's leading testing and certification company", color: "bg-rose-50 border-rose-200 text-rose-700" },
                    { cert: "IEC 61215", desc: "Solar panel design qualification — ensures panels meet performance and durability standards", color: "bg-purple-50 border-purple-200 text-purple-700" },
                    { cert: "UL Listed", desc: "Underwriters Laboratories — North American safety certification for electrical products", color: "bg-orange-50 border-orange-200 text-orange-700" },
                    { cert: "TÜV Certified", desc: "German technical inspection — rigorous safety and quality testing for global markets", color: "bg-teal-50 border-teal-200 text-teal-700" },
                    { cert: "IATF 16949", desc: "Automotive Quality Management — highest standard for automotive supply chain quality", color: "bg-indigo-50 border-indigo-200 text-indigo-700" },
                  ].map(c => (
                    <div key={c.cert} className={`border rounded-xl p-4 ${c.color}`}>
                      <p className="font-extrabold text-sm mb-1">{c.cert}</p>
                      <p className="text-xs leading-relaxed opacity-80">{c.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── Product Catalog Tab ── */}
          {activeTab === "products" && (
            <>
              <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-2xl font-extrabold text-gray-900 mb-1">Solar &amp; EV Product Catalog</h2>
                  <p className="text-gray-500 text-sm">Click any product for full specs, factory details, and sample ordering</p>
                </div>
                <button
                  onClick={() => setShowGlobalRFQ(true)}
                  className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap shrink-0"
                >
                  <i className="ri-file-list-3-line"></i> Request Bulk Quote
                </button>
              </div>

              {/* Category filter */}
              <div className="flex gap-2 flex-wrap mb-6">
                {catTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveCat(tab.id)}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                      activeCat === tab.id
                        ? "bg-amber-400 text-white border-amber-400"
                        : "bg-white text-gray-500 border-gray-200 hover:border-amber-300"
                    }`}
                  >
                    <i className={tab.icon}></i>{tab.label}
                  </button>
                ))}
              </div>

              <p className="text-xs text-gray-400 mb-4">{filteredProducts.length} products found</p>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-10" data-product-shop>
                {filteredProducts.map(p => (
                  <ProductCard key={p.id} product={p} onClick={() => setDetailProduct(p)} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* RFQ CTA Banner */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=solar%20energy%20farm%20electric%20vehicles%20golf%20cart%20e-bike%20factory%20aerial%20view%20sunset%20golden%20hour%20professional%20photography%20wide%20angle%20beautiful%20landscape&width=1400&height=320&seq=sevctabanner&orientation=landscape"
              alt="RFQ Banner"
              className="w-full h-64 md:h-80 object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-transparent"></div>
            <div className="absolute inset-0 flex items-center px-8 md:px-16">
              <div className="max-w-xl">
                <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">
                  Need a Bulk Quote?<br />
                  <span className="text-amber-400">Send One RFQ to 500+ Factories</span>
                </h2>
                <p className="text-white/80 text-sm mb-6 leading-relaxed">
                  Submit your requirements once and receive competitive quotes from hundreds of verified solar and EV manufacturers. Free, fast, and no commitment required.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setShowGlobalRFQ(true)}
                    className="flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-8 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-file-list-3-line"></i> Submit Free RFQ Now
                  </button>
                  <Link
                    to="/"
                    className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold px-8 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-home-line"></i> Back to Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why source from us */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3">Why Source Solar &amp; EV from CrownGlobal?</h2>
            <p className="text-gray-500 text-sm max-w-xl mx-auto">We connect you directly with the world's top manufacturers — no middlemen, no markups, just factory-direct pricing.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: "ri-shield-check-line", title: "Factory Verified", desc: "Every factory is audited for certifications, production capacity, and quality standards before listing.", color: "text-green-500 bg-green-50" },
              { icon: "ri-money-dollar-circle-line", title: "Factory-Direct Pricing", desc: "Skip the middlemen. Buy directly from manufacturers at wholesale prices with no hidden fees.", color: "text-amber-500 bg-amber-50" },
              { icon: "ri-message-3-line", title: "Instant Communication", desc: "Chat directly with factory sales teams in real-time. Get answers about specs, MOQ, and lead times fast.", color: "text-sky-500 bg-sky-50" },
              { icon: "ri-lock-line", title: "Secure Transactions", desc: "Payment held in escrow until you confirm receipt. Full buyer protection on every order.", color: "text-rose-500 bg-rose-50" },
            ].map(f => (
              <div key={f.title} className="bg-gray-50 rounded-2xl p-6 text-center">
                <div className={`w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-4 ${f.color}`}>
                  <i className={`${f.icon} text-2xl`}></i>
                </div>
                <h3 className="font-extrabold text-gray-900 text-base mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Modals */}
      {(showGlobalRFQ || rfqFactory !== undefined) && (
        <RFQModal
          preselectedFactory={rfqFactory ?? null}
          onClose={() => { setShowGlobalRFQ(false); setRfqFactory(undefined); }}
        />
      )}
      {chatFactory && (
        <FactoryChatModal factory={chatFactory} onClose={() => setChatFactory(null)} />
      )}
      {detailProduct && (
        <ProductDetailModal
          product={detailProduct}
          onClose={() => setDetailProduct(null)}
          onOrder={() => setDetailProduct(null)}
        />
      )}
    </div>
  );
}
